import { useState, useEffect } from "react";

/* PACKAGES */
import { motion } from "framer-motion";
import * as yup from "yup";
import { useFormik } from "formik";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import moment from "moment";
import Select from "react-select";
import { State } from "country-state-city";
import CopyToClipboard from "react-copy-to-clipboard";

/* MATERIAL TAILWIND COMPONENTS */
import {
  Tab,
  TabPanel,
  Tabs,
  TabsBody,
  TabsHeader,
  Button,
  Dialog,
  DialogBody,
  Tooltip,
} from "@material-tailwind/react";

/* USER DEFINED COMPONENTS */
import { FormInput, PhoneInputField } from "../../components/core/input";
import { FormButton } from "../../components/core/buttons";
import ProductBox from "../../components/others/product";

/* ICONS */
import { IoEye, IoEyeOff, IoClose } from "react-icons/io5";
import { FaRegCircleCheck } from "react-icons/fa6";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { BiCopy } from "react-icons/bi";
import { MdCancel } from "react-icons/md";

/* DATA */
import { dashboardTabs, sampleOrders, couponCodes } from "../../data/dashboard";
import bestsellers from "../../data/bestsellers";

/* CONTAINER ANIMATION */
const containerAnimation = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 1 },
  },
};

/* DIV ANIMATION */
const animation = {
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
  hidden: {
    opacity: 0,
    y: 20,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
};

/* VALIDATION */
const validation = yup.object().shape({
  image: yup.object().required("Image is required"),
  firstName: yup
    .string()
    .min(2, "Too short")
    .required("First name is required"),
  lastName: yup.string().min(2, "Too short").required("Last name is required"),
  email: yup.string().email().required("Last name is required"),
  phone: yup.string().min(2, "Too short").required("Phone number is required"),
  password: yup.string().min(2, "Too short").required("Password is required"),
});

/* ADD ADDRESS DIALOG */
const addressValidation = yup.object().shape({
  firstName: yup
    .string()
    .min(2, "Too short")
    .required("First name is required"),
  lastName: yup.string().min(2, "Too short").required("Last name is required"),
  addressLine1: yup
    .string()
    .min(2, "Too short")
    .required("Address line 1 is required"),
  city: yup.string().min(2, "Too short").required("City is required"),
  state: yup.string().required("State is required"),
  zipCode: yup.string().min(2, "Too short").required("Pincode is required"),
  country: yup.string().required("Country is required"),
});

const Dashboard = () => {
  const [password, setPassword] = useState(false);
  const [addNewAddress, setAddNewAddress] = useState(false);
  const [states, setStates] = useState([]);

  /* USER SUMMARY */
  const UserSummary = (props) => {
    return (
      <div className="bg-white shadow-md rounded-lg border border-greyBorder px-5 py-3 block xl:flex lg:flex items-center justify-between">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 rounded-full border-2 border-primaryRed p-1">
            <img
              src={props.image}
              className="w-full h-full rounded-full object-cover"
              alt={props.name}
            />
          </div>
          <div>
            <h2 className="text-xl font-source font-bold text-black leading-tight">
              {props.name}
            </h2>
            <h3 className="text-base font-source font-normal text-headerGrey">
              {props.email}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-8 justify-center mt-5 xl:mt-0 lg:mt-0">
          <div className="text-center">
            <h2 className="text-2xl font-source font-bold text-black leading-tight">
              {props.rewardPoints}
            </h2>
            <h3 className="text-sm font-source font-normal text-headerGrey text-opacity-80">
              Reward Points
            </h3>
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-source font-bold text-black leading-tight">
              {props.memberSince}
            </h2>
            <h3 className="text-sm font-source font-normal text-headerGrey text-opacity-80">
              Member Since
            </h3>
          </div>
        </div>
      </div>
    );
  };

  /* ADDRESS BOX */
  const AddressBox = (props) => {
    return (
      <div className="border border-greyColor shadow p-5 rounded-lg">
        <div>
          <h5 className="text-base font-source font-bold text-black">
            {props.name}
          </h5>
          <h5 className="text-sm font-source font-normal text-headerGrey">
            {props.email}
          </h5>
          <h5 className="text-sm font-source font-normal text-headerGrey">
            {props.phone}
          </h5>
        </div>
        <div className="w-10 h-[0.1vh] bg-gray-400 mt-3" />
        <div className="mt-3">
          <h5 className="text-sm font-source font-normal text-headerGrey">
            {props.houseNo}
          </h5>
          <h5 className="text-sm font-source font-normal text-headerGrey">
            {props.streetLine}
          </h5>
          <h5 className="text-sm font-source font-normal text-headerGrey">
            {props.country}
          </h5>
        </div>
        <div className="mt-3 flex items-center">
          <p className="cursor-pointer text-headerGrey font-source font-normal italic text-sm hover:underline mr-5">
            Edit
          </p>
          <p className="cursor-pointer text-red-500 font-source font-normal italic text-sm hover:underline">
            Delete
          </p>
        </div>
      </div>
    );
  };

  /* ACCOUNT INFORMATION HANDLER */
  const accountInfo = useFormik({
    initialValues: {
      image: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: validation,
    onSubmit: (values) => {
      console.log("Submitted values: ", values);
    },
  });

  /* ORDER TABLE COLUMNS */
  const ordersTable = [
    {
      name: "Order ID",
      selector: (row) => (
        <Link
          href="/dashboard"
          className="text-sm font-source font-normal text-logo hover:underline"
        >
          {row.orderID}
        </Link>
      ),
    },
    {
      name: "Order Date",
      selector: (row) => (
        <p className="text-sm font-source font-normal text-black">
          {moment(row.createdAt).format("DD/MM/YYYY  hh:mm A")}
        </p>
      ),
    },
    {
      name: "Product Name",
      grow: 2,
      selector: (row) => (
        <p className="text-sm font-source font-semibold text-black">
          {row?.productName}
        </p>
      ),
    },
    {
      name: "Amount",
      selector: (row) => (
        <p className="text-sm font-source font-normal text-black">
          {row.amount}
        </p>
      ),
    },
    {
      name: "Status",
      selector: (row) => (
        <>
          {row?.status === "Delivered" && (
            <p className="text-sm font-source font-semibold text-green-500">
              {row?.status}
            </p>
          )}
          {row?.status === "In transit" && (
            <p className="text-sm font-source font-semibold text-red-500">
              {row?.status}
            </p>
          )}
          {row?.status === "Order shipped" && (
            <p className="text-sm font-source font-semibold text-primaryRed">
              {row?.status}
            </p>
          )}
        </>
      ),
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="flex items-center gap-2">
          <Tooltip content="View order details">
            <Button className="text-textGrey rounded-full p-2 bg-white hover:bg-black hover:bg-opacity-5 shadow-none hover:shadow-none">
              <IoEye className="w-4 h-4 text-textGrey" />
            </Button>
          </Tooltip>
          <Tooltip content="Cancel order">
            <Button className="text-textGrey rounded-full p-2 bg-white hover:bg-black hover:bg-opacity-5 shadow-none hover:shadow-none">
              <MdCancel className="w-4 h-4 text-textGrey" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  /* COUPON CODES COLUMNS */
  const couponCodesTable = [
    {
      name: "Coupon code",
      selector: (row) => (
        <p className="text-sm font-source font-normal text-black">
          {row.couponCode}
        </p>
      ),
    },
    {
      name: "Discount",
      selector: (row) => (
        <p className="text-sm font-source font-semibold text-black">
          {row.discount}
        </p>
      ),
    },
    {
      name: "Date",
      selector: (row) => (
        <p className="text-sm font-source font-normal text-black">{row.date}</p>
      ),
    },
  ];

  /* ADD NEW ADDRESS FORM HANDLER */
  const newAddressInfo = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
      country: "India",
    },
    validationSchema: addressValidation,
    onSubmit: (values) => {
      console.log("Submitted values: ", values);
    },
  });

  /* USE EFFECT */
  useEffect(() => {
    let tempArr = [];
    State.getStatesOfCountry("IN").forEach((data) => {
      tempArr.push({ label: data.name, value: data.name });
    });
    setStates(tempArr);
  }, []);

  const [oldPassword, setOldPassword] = useState(false);
  const [newPassword, setNewPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState(false);

  return (
    <motion.section
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="bg-white py-6 xl:py-14 lg:py-14"
    >
      <div className="container">
        <Tabs
          value="dashboard"
          orientation="vertical"
          className="w-full block overflow-visible"
        >
          <div className="grid grid-cols-1 xl:grid-cols-4 lg:grid-cols-4 gap-0 xl:gap-10 lg:gap-10">
            <motion.div variants={animation} className="col-span-1 h-fit">
              <div className="h-fit sticky top-5">
                <TabsHeader
                  className="bg-greyBg rounded-lg border border-greyColor text-black relative account-tabs"
                  indicatorProps={{
                    className: "text-black bg-white shadow-md",
                  }}
                >
                  {dashboardTabs?.map((tab) => (
                    <Tab
                      key={tab?.key}
                      value={tab?.value}
                      id={tab?.key}
                      className="p-4 text-left font-source font-semibold detail-tab"
                    >
                      {tab?.icon}
                      {tab?.label}
                    </Tab>
                  ))}
                </TabsHeader>
                <Button className="w-full shadow-none hover:shadow-none bg-black text-base font-source font-semibold mt-3 normal-case font-medium">
                  Log out
                </Button>
              </div>
            </motion.div>
            <motion.div
              variants={animation}
              className="col-span-3 mt-5 xl:mt-0 lg:mt-0"
            >
              <TabsBody className="pb-2">
                {/* DASHBOARD */}
                <TabPanel value="dashboard" className="p-0">
                  {/* USER SUMMARY */}
                  <UserSummary
                    image="https://images.unsplash.com/photo-1438761681033-6461ffad8d80"
                    name="Darsh Shah"
                    email="darsh@roundtechsquare.com"
                    rewardPoints="100"
                    memberSince="2024"
                  />

                  {/* ACCOUNT INFORMATION */}
                  <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-5 xl:gap-10 lg:gap-10 mt-10">
                    <div className="col-span-1">
                      <h3 className="text-xl font-fraunces-bold tracking-tight text-black">
                        Account Information
                      </h3>
                      <h3 className="text-sm font-source font-normal text-headerGrey text-opacity-80 leading-normal">
                        Efficiently manage and update your account information
                        right here for a seamless experience.
                      </h3>
                    </div>
                    <div className="col-span-2">
                      <form>
                        <div className="bg-white shadow-md rounded-lg border border-greyBorder p-5 overflow-visible">
                          <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-5">
                            <div className="col-span-1">
                              <label className="text-sm font-source font-semibold text-black">
                                Profile Image
                              </label>
                              <div className="flex items-center gap-2 mt-1">
                                <img
                                  src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80"
                                  className="w-12 h-12 rounded-full object-cover"
                                  alt="Darsh Shah"
                                />
                                <label
                                  onClick={() => {
                                    document
                                      .getElementById("profileImage")
                                      .click();
                                  }}
                                  className="text-xs font-source font-bold bg-[#F5F5F5] rounded px-2 py-1 border border-greyColor"
                                >
                                  Upload image
                                </label>
                                <input
                                  type="file"
                                  id="profileImage"
                                  accept="image/*"
                                  className="hidden"
                                />
                              </div>
                            </div>
                            <div className="col-span-1">
                              <div className="form-group">
                                <label className="text-sm font-source font-semibold text-black">
                                  First Name
                                </label>
                                <div className="mt-1">
                                  <FormInput
                                    type="text"
                                    name="firstName"
                                    id="firstName"
                                    placeholder="First name"
                                    value={accountInfo.values.firstName}
                                    onChange={accountInfo.handleChange}
                                    errorHandler={
                                      accountInfo.errors.firstName &&
                                      accountInfo.touched.firstName
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-span-1">
                              <div className="form-group">
                                <label className="text-sm font-source font-semibold text-black">
                                  Last Name
                                </label>
                                <div className="mt-1">
                                  <FormInput
                                    type="text"
                                    name="lastName"
                                    id="lastName"
                                    placeholder="Last name"
                                    value={accountInfo.values.lastName}
                                    onChange={accountInfo.handleChange}
                                    errorHandler={
                                      accountInfo.errors.lastName &&
                                      accountInfo.touched.lastName
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mt-5 grid grid-cols-2 gap-5">
                            <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                              <div className="form-group">
                                <label className="text-sm font-source font-semibold text-black">
                                  Email address
                                </label>
                                <div className="mt-1">
                                  <FormInput
                                    type="email"
                                    name="email"
                                    id="email"
                                    placeholder="Email address"
                                    value={accountInfo.values.email}
                                    onChange={accountInfo.handleChange}
                                    errorHandler={
                                      accountInfo.errors.email &&
                                      accountInfo.touched.email
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                              <label className="text-sm font-source font-semibold text-black">
                                Phone
                              </label>
                              <div className="mt-1">
                                <PhoneInputField
                                  value={accountInfo.values.phone}
                                  onChange={accountInfo.handleChange}
                                  errorHandler={
                                    accountInfo.errors.phone &&
                                    accountInfo.touched.phone
                                  }
                                />
                              </div>
                            </div>
                            <div className="col-span-2">
                              <div className="form-group">
                                <label className="text-sm font-source font-semibold text-black">
                                  Password
                                </label>
                                <div className="mt-1 relative">
                                  <FormInput
                                    type={password ? "text" : "password"}
                                    name="password"
                                    id="password"
                                    placeholder="Type your password"
                                    value={accountInfo.values.password}
                                    onChange={accountInfo.handleChange}
                                    errorHandler={
                                      accountInfo.errors.password &&
                                      accountInfo.touched.password
                                    }
                                  />
                                  <Button
                                    className="p-2 rounded-full shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-10 bg-transparent !absolute top-2 right-2"
                                    onClick={() => setPassword(!password)}
                                  >
                                    {password ? (
                                      <IoEyeOff className="w-5 h-5 text-headerGrey" />
                                    ) : (
                                      <IoEye className="w-5 h-5 text-headerGrey" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mt-5">
                            <FormButton type="button" label="Save details" />
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </TabPanel>

                {/* CHANGE PASSWORD */}
                <TabPanel value="reset-password" className="p-0">
                  <h3 className="text-2xl font-fraunces-bold tracking-tight text-black">
                    Change Password
                  </h3>
                  <p className="text-base font-source font-normal text-headerGrey">
                    Reset your account password seamlessly in two steps
                  </p>
                  <div className="mt-5">
                    <div className="grid grid-cols-3 gap-5">
                      <div className="col-span-1">
                        <div className="form-group">
                          <label className="text-sm font-source font-semibold text-black">
                            Old Password
                          </label>
                          <div className="mt-1 relative">
                            <FormInput
                              type={oldPassword ? "text" : "password"}
                              name="password"
                              id="password"
                              placeholder="Type your password"
                            />
                            <Button
                              className="p-2 rounded-full shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-10 bg-transparent !absolute top-2 right-2"
                              onClick={() => setOldPassword(!oldPassword)}
                            >
                              {oldPassword ? (
                                <IoEyeOff className="w-5 h-5 text-headerGrey" />
                              ) : (
                                <IoEye className="w-5 h-5 text-headerGrey" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label className="text-sm font-source font-semibold text-black">
                            New Password
                          </label>
                          <div className="mt-1 relative">
                            <FormInput
                              type={newPassword ? "text" : "password"}
                              name="password"
                              id="password"
                              placeholder="Type your password"
                            />
                            <Button
                              className="p-2 rounded-full shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-10 bg-transparent !absolute top-2 right-2"
                              onClick={() => setNewPassword(!newPassword)}
                            >
                              {newPassword ? (
                                <IoEyeOff className="w-5 h-5 text-headerGrey" />
                              ) : (
                                <IoEye className="w-5 h-5 text-headerGrey" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label className="text-sm font-source font-semibold text-black">
                            Confirm New Password
                          </label>
                          <div className="mt-1 relative">
                            <FormInput
                              type={confirmPassword ? "text" : "password"}
                              name="password"
                              id="password"
                              placeholder="Type your password"
                            />
                            <Button
                              className="p-2 rounded-full shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-10 bg-transparent !absolute top-2 right-2"
                              onClick={() =>
                                setConfirmPassword(!confirmPassword)
                              }
                            >
                              {confirmPassword ? (
                                <IoEyeOff className="w-5 h-5 text-headerGrey" />
                              ) : (
                                <IoEye className="w-5 h-5 text-headerGrey" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabPanel>

                {/* ORDERS */}
                <TabPanel value="my-orders" className="p-0">
                  <h3 className="text-2xl font-fraunces-bold tracking-tight text-black">
                    Your orders
                  </h3>
                  <p className="text-base font-source font-normal text-headerGrey">
                    Track, manage and review your purchases effortlessly here
                    for seamless shopping.
                  </p>
                  <div className="mt-5">
                    <DataTable
                      columns={ordersTable}
                      data={sampleOrders}
                      className="react-table"
                      noDataComponent={
                        <div className="col-span-4 w-full py-3 border border-primaryRed bg-primaryRed bg-opacity-5 rounded-md">
                          <p className="text-center text-sm font-source font-semibold text-black">
                            Looks like your order history is empty. Time to fill
                            it with the joy of shopping! Explore our collection
                            and start adding items to your cart. Happy shopping!
                          </p>
                        </div>
                      }
                      pagination
                      paginationTotalRows={5}
                      responsive
                    />
                  </div>
                </TabPanel>

                {/* WISHLIST */}
                <TabPanel value="my-wishlist" className="p-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-fraunces-bold tracking-tight text-black">
                        Your wishlist
                      </h3>
                      <p className="text-base font-source font-normal text-headerGrey">
                        Add your favorite products to the cart effortlessly from
                        here and proceed to checkout.
                      </p>
                    </div>
                    <Button className="shadow-none hover:shadow-none bg-black rounded-md text-sm font-source font-semibold text-white normal-case px-5 py-2">
                      Add all items to cart
                    </Button>
                  </div>
                  <div className="mt-5 grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-5">
                    {bestsellers.slice(0, 2).map((data) => (
                      <div className="col-span-1" key={data.id}>
                        <ProductBox
                          image={data.image}
                          name={data.name}
                          ratings={data.ratings}
                          ratingsPeople={data.ratingsPeople}
                          price={data.price}
                          mrpPrice={data.mrpPrice}
                        />
                      </div>
                    ))}
                  </div>
                </TabPanel>

                {/* ADDRESS BOOK */}
                <TabPanel value="address-book" className="p-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-fraunces-bold tracking-tight text-black">
                        Address book
                      </h3>
                      <p className="text-base font-source font-normal text-headerGrey">
                        Add and manage your addresses for effortless checkout.
                      </p>
                    </div>
                    <Button
                      className="shadow-none hover:shadow-none text-white text-sm font-source font-semibold normal-case font-normal bg-black px-5 py-3"
                      onClick={() => setAddNewAddress(!addNewAddress)}
                    >
                      Add new address
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-4 lg:grid-cols-4 gap-5 mt-5">
                    <div className="col-span-1">
                      <AddressBox
                        name="Hittarth Solanki"
                        email="hittarth@roundtechsquare.com"
                        phone="+91 76523347685"
                        houseNo="Australia Avenue"
                        streetLine="Sydney Olympic Park, New South Wales, 2127"
                        country="Australia"
                      />
                    </div>
                  </div>
                </TabPanel>

                {/* LOYALTY PROGRAM */}
                <TabPanel value="loyalty-program" className="p-0">
                  <div className="bg-white shadow-md rounded-lg border border-greyColor">
                    <div className="px-5 xl:px-8 lg:px-8 py-3 block xl:flex lg:flex items-center justify-between border-b border-b-greyColor">
                      <div>
                        <h2 className="text-xl font-source font-bold text-black leading-tight">
                          You are a member since 2023
                        </h2>
                        <h3 className="text-sm font-source font-normal text-headerGrey">
                          Enjoy exclusive perks, discounts, and special treats
                          as our valued member.
                        </h3>
                      </div>
                      <div className="flex items-center justify-center gap-8 mt-5 xl:mt-0 lg:mt-0">
                        <div className="text-center">
                          <h2 className="text-2xl font-source font-bold text-black leading-tight">
                            10
                          </h2>
                          <h3 className="text-sm font-source font-normal text-headerGrey">
                            Reward Points
                          </h3>
                        </div>
                        <div className="text-center">
                          <h2 className="text-2xl font-source font-bold text-black leading-tight">
                            5
                          </h2>
                          <h3 className="text-sm font-source font-normal text-headerGrey">
                            Points spent
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 xl:px-8 lg:px-8 py-3">
                      <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 items-center gap-0 xl:gap-8 lg:gap-8">
                        <div className="col-span-1">
                          <div className="border border-primaryRed border-opacity-40 shadow-md rounded-lg">
                            <div className="p-5 bg-primaryRed bg-opacity-10 relative">
                              <h5 className="text-xl font-source font-bold text-black">
                                ₹10 off on entire order
                              </h5>
                              <div className="mt-1">
                                <input
                                  type="number"
                                  name="points"
                                  placeholder="Enter the points to redeem"
                                  className="block w-full border border-greyBorder px-3 py-2 text-sm rounded-md font-source font-normal text-black"
                                />
                                <span className="text-black text-sm font-source font-normal mt-1 block">
                                  Available points: 10
                                </span>
                              </div>
                            </div>
                            <div className="bg-transparent flex items-center gap-3 py-3 px-5">
                              <Button className="bg-black font-source font-normal text-sm text-white shadow-none hover:shadow-none py-2 px-5 normal-case rounded-md">
                                Get coupon code
                              </Button>
                              <h5 className="textt-sm font-source font-normal text-black">
                                with 10 points
                              </h5>
                            </div>
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="border-l-0 xl:border-l lg:border-l border-greyColor border-opacity-80 px-0 xl:px-8 lg:px-8 mt-5 xl:mt-0 lg:mt-0">
                            <div className="rounded-md px-5 py-2 bg-logo bg-opacity-5 border border-logo">
                              <h5 className="text-black font-source font-normal text-sm">
                                Each{" "}
                                <b className="font-source font-medium">
                                  ₹50.00
                                </b>{" "}
                                spent for your order will earn{" "}
                                <b className="font-source font-medium">
                                  1 point.
                                </b>
                              </h5>
                              <h5 className="text-black font-source font-normal text-sm">
                                Each{" "}
                                <b className="font-source font-medium">
                                  1 point
                                </b>{" "}
                                can be redeemed for{" "}
                                <b className="font-source font-medium">
                                  ₹1.00.
                                </b>
                              </h5>
                            </div>
                            <h5 className="text-base font-source font-semibold text-black leading-snug mt-5">
                              Accumulate reward points, select a coupon reward,
                              redeem points, receive code, apply it during
                              checkout to enjoy the discount.
                            </h5>
                            <div className="flex items-center justify-center xl:justify-start lg:justify-start gap-10 bg-greyBorder bg-opacity-50 py-2 px-5 rounded-md mt-5 w-fit">
                              <p className="text-sm font-source font-normal text-black leading-none">
                                KIDSTREET50
                              </p>
                              <CopyToClipboard text="Coupon code copied">
                                <Button className="p-2 rounded-full bg-transparent hover:bg-[#101727] hover:bg-opacity-5 shadow-none hover:shadow-none">
                                  <BiCopy className="w-4 h-4 text-[#465B52]" />
                                </Button>
                              </CopyToClipboard>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabPanel>

                {/* COUPON CODES */}
                <TabPanel value="coupon-codes" className="p-0">
                  <div>
                    <h3 className="text-2xl font-fraunces-bold tracking-tight text-black">
                      Your coupon codes
                    </h3>
                    <p className="text-base font-source font-normal text-headerGrey">
                      Unlock savings with exclusive promo codes! Instant
                      discounts on your favorite products await in account.
                    </p>
                    <div className="mt-5">
                      <DataTable
                        columns={couponCodesTable}
                        data={couponCodes}
                        className="react-table"
                        pagination
                        responsive
                      />
                    </div>
                  </div>
                </TabPanel>
              </TabsBody>
            </motion.div>
          </div>
        </Tabs>
      </div>

      {/* ADD NEW ADDRESS DIALOG */}
      <Dialog
        open={addNewAddress}
        handler={() => setAddNewAddress(!addNewAddress)}
        size="sm"
        dismiss={{
          enabled: false,
        }}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
        className="!min-w-[90%] xl:!min-w-max lg:!min-w-max"
      >
        <DialogBody className="p-0">
          <div className="border-b border-greyBorder p-5 flex items-center justify-between">
            <h5 className="text-lg font-source font-semibold text-black">
              Add Address
            </h5>
            <Button
              className="p-0 bg-white shadow-none hover:shadow-none text-headerGrey"
              onClick={() => setAddNewAddress(!addNewAddress)}
            >
              <IoClose className="w-5 h-5" />
            </Button>
          </div>

          <form className="p-0" onSubmit={newAddressInfo.handleSubmit}>
            <div className="grid grid-cols-2 gap-3 p-5">
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="text"
                  name="firstName"
                  id="firstName"
                  placeholder="First name"
                  value={newAddressInfo.values.firstName}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.firstName &&
                    newAddressInfo.touched.firstName
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="text"
                  name="lastName"
                  id="lastName"
                  placeholder="Last name"
                  value={newAddressInfo.values.lastName}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.lastName &&
                    newAddressInfo.touched.lastName
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <div className="form-group">
                  <FormInput
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Email address"
                    value={newAddressInfo.values?.email}
                    onChange={newAddressInfo.handleChange}
                    errorHandler={
                      newAddressInfo.errors.email &&
                      newAddressInfo.touched.email
                    }
                  />
                </div>
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <PhoneInputField
                  value={newAddressInfo.values.phone}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.phone && newAddressInfo.touched.phone
                  }
                />
              </div>
              <div className="col-span-2">
                <FormInput
                  type="text"
                  name="addressLine1"
                  id="addressLine1"
                  placeholder="Address Line 1"
                  value={newAddressInfo.values.addressLine1}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.addressLine1 &&
                    newAddressInfo.touched.addressLine1
                  }
                />
              </div>
              <div className="col-span-2">
                <FormInput
                  type="text"
                  name="addressLine2"
                  id="addressLine2"
                  placeholder="Address Line 2 (optional)"
                  value={newAddressInfo.values.addressLine2}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.addressLine2 &&
                    newAddressInfo.touched.addressLine2
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="text"
                  name="city"
                  id="city"
                  placeholder="City"
                  value={newAddressInfo.values.city}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.city && newAddressInfo.touched.city
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <div className="relative">
                  <Select
                    options={states}
                    placeholder="State"
                    className={`${newAddressInfo.errors.state && "error"}`}
                    classNamePrefix="state"
                    name="state"
                    id="state"
                    value={newAddressInfo.values.state}
                    onChange={(e) => {
                      newAddressInfo.setFieldValue("state", e);
                    }}
                  />

                  {newAddressInfo.errors.state &&
                    newAddressInfo.touched.state && (
                      <div className="absolute top-3 right-8">
                        <AiOutlineExclamationCircle className="w-5 h-5 text-red-500" />
                      </div>
                    )}

                  {newAddressInfo.values.state !== "" && (
                    <div className="absolute top-3 right-8">
                      <FaRegCircleCheck className="w-5 h-5 text-green-500" />
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="number"
                  name="zipCode"
                  id="zipCode"
                  placeholder="Zipcode"
                  value={newAddressInfo.values.zipCode}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.zipCode &&
                    newAddressInfo.touched.zipCode
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="text"
                  name="country"
                  id="country"
                  placeholder="Country"
                  value={newAddressInfo.values.country}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.country &&
                    newAddressInfo.touched.country
                  }
                />
              </div>
            </div>

            <div className="border-t border-greyBorder p-5 flex items-center justify-end">
              <FormButton type="submit" label="Submit details" />
            </div>
          </form>
        </DialogBody>
      </Dialog>
    </motion.section>
  );
};

export default Dashboard;
